import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";

export type DashboardComponentKey =
  | "my_tasks"
  | "meetings"
  | "reminders"
  | "counts"
  | "admin_callouts"
  | "location_strip"
  | "behavior_alerts"
  | "metric_widgets";

export const DASHBOARD_COMPONENT_KEYS: DashboardComponentKey[] = [
  "my_tasks",
  "meetings",
  "reminders",
  "counts",
  "admin_callouts",
  "location_strip",
  "behavior_alerts",
  "metric_widgets",
];

export const DASHBOARD_COMPONENT_LABELS: Record<DashboardComponentKey, string> = {
  my_tasks: "My Tasks",
  meetings: "Meetings",
  reminders: "Reminders",
  counts: "Counts (nav + widget)",
  admin_callouts: "Admin Callouts",
  location_strip: "Location Strip",
  behavior_alerts: "Behavior Alerts",
  metric_widgets: "Metric Widgets",
};

/**
 * Optional role allowlist per component key.
 * If a key is present here, the component only appears as a configurable
 * option for the listed role slugs in the visibility dialog.
 * If a key is absent, it is configurable for every managed role.
 *
 * This does NOT affect runtime visibility — the dashboard's own hardcoded
 * guards (e.g. `isAdmin`) remain the final authority. This map only controls
 * what appears as a checkbox in the admin config UI.
 */
export const COMPONENT_ROLE_RESTRICTIONS: Partial<Record<DashboardComponentKey, string[]>> = {
  admin_callouts: ["admin"],
};

interface DashboardVisibilityResponse {
  visibleKeys: DashboardComponentKey[];
  roleSlug: string;
}

export function useDashboardComponents(): {
  visible: Set<DashboardComponentKey>;
  isLoading: boolean;
} {
  const { user } = useAuth();

  const { data, isLoading } = useQuery<DashboardVisibilityResponse>({
    queryKey: ["/api/app-settings/dashboard-visibility"],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const visible = new Set<DashboardComponentKey>(
    data?.visibleKeys ?? DASHBOARD_COMPONENT_KEYS
  );

  return { visible, isLoading };
}
